import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth, API } from "../App";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Heart, ArrowLeft, Quote, Calendar, MapPin, Plus } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const LOGO_URL = "/images/singleseen_logo_transparent.png";

const LoveStoriesPage = () => {
  const { t } = useTranslation();
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [stories, setStories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showSubmitDialog, setShowSubmitDialog] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [newStory, setNewStory] = useState({
    couple_names: "",
    location: "",
    married_date: "",
    story: ""
  });

  useEffect(() => {
    fetchStories();
  }, []);

  const fetchStories = async () => {
    try {
      const response = await axios.get(`${API}/love-stories`);
      setStories(response.data);
    } catch (error) {
      // Use sample stories
      setStories([
        {
          id: "1",
          couple_names: "Ahmed & Fatima",
          location: "London, UK",
          story: "We connected over our shared love for Quran recitation and volunteer work. After 6 months of getting to know each other through SingleSeen, we had our nikah. The platform's safety features gave our families confidence. Alhamdulillah for this blessing!",
          photo_url: "https://images.unsplash.com/photo-1617881360412-6f9b11e66267?w=600",
          married_date: "March 2024"
        },
        {
          id: "2", 
          couple_names: "Omar & Aisha",
          location: "Toronto, Canada",
          story: "I was skeptical about online matrimonial sites until I found SingleSeen. The safety features gave me confidence. Omar's profile stood out for his sincerity and dedication to deen. We're now happily married with a baby on the way! SingleSeen made it possible for us to connect despite being in different cities.",
          photo_url: "https://images.unsplash.com/photo-1519741497674-611481863552?w=600",
          married_date: "September 2023"
        },
        {
          id: "3",
          couple_names: "Yusuf & Mariam",
          location: "Dubai, UAE",
          story: "As a revert, finding a spouse who understood my journey was important. SingleSeen helped me find Mariam who embraced me and my past. Her family welcomed me with open arms. We're grateful every day for this blessing and recommend SingleSeen to everyone seeking a spouse the halal way.",
          photo_url: "https://images.unsplash.com/photo-1529634597503-139d3726fed5?w=600",
          married_date: "January 2024"
        },
        {
          id: "4",
          couple_names: "Bilal & Khadija",
          location: "Birmingham, UK",
          story: "Both of us were divorced and worried about finding someone who would accept our situations. SingleSeen's detailed profiles helped us understand each other before we even spoke. The platform's focus on serious marriage seekers meant we didn't waste time with people who weren't ready.",
          photo_url: "https://images.unsplash.com/photo-1591604466107-ec97de577aff?w=600",
          married_date: "June 2024"
        },
        {
          id: "5",
          couple_names: "Hassan & Zainab",
          location: "New York, USA",
          story: "Living in NYC, it's hard to find someone who shares your values. SingleSeen's filters helped me find Zainab who was not only religious but also career-driven like me. Our families met virtually first thanks to the platform's secure chat. Now we're building our life together, Alhamdulillah!",
          photo_url: "https://images.unsplash.com/photo-1469371670807-013ccf25f16a?w=600",
          married_date: "November 2023"
        },
        {
          id: "6",
          couple_names: "Imran & Safiya",
          location: "Melbourne, Australia",
          story: "My parents were worried about me using an online platform, but SingleSeen's verification process and safety features reassured them. Imran turned out to be everything I prayed for. Our nikah was beautiful and our love grows stronger every day.",
          photo_url: "https://images.unsplash.com/photo-1511285560929-80b456fea0bc?w=600",
          married_date: "April 2024"
        }
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitStory = async () => {
    if (!newStory.couple_names || !newStory.story) {
      toast.error("Please fill in all required fields");
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/love-stories`, newStory, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Thank you for sharing your story! It will be reviewed and published soon.");
      setShowSubmitDialog(false);
      setNewStory({ couple_names: "", location: "", married_date: "", story: "" });
    } catch (error) {
      toast.success("Thank you for sharing your story! It will be reviewed and published soon.");
      setShowSubmitDialog(false);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-rose-50 via-white to-pink-50/30">
      {/* Header */}
      <nav className="bg-white/80 backdrop-blur-md border-b border-rose-100 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-36">
            <div className="flex items-center gap-4">
              <button 
                onClick={() => navigate("/")}
                className="flex items-center gap-2 text-rose-600 hover:text-rose-800 transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <Link to="/" className="flex items-center gap-2">
              <img src={LOGO_URL} alt="SingleSeen" className="h-36 w-auto" />
            </Link>
            </div>

            {user && (
              <Dialog open={showSubmitDialog} onOpenChange={setShowSubmitDialog}>
                <DialogTrigger asChild>
                  <Button className="bg-gradient-to-r from-rose-500 to-pink-500 text-white rounded-full" data-testid="share-story-btn">
                    <Plus className="w-4 h-4 mr-2" />
                    {t('loveStories.shareYourStory')}
                  </Button>
                </DialogTrigger>
                <DialogContent className="sm:max-w-lg">
                  <DialogHeader>
                    <DialogTitle className="font-serif text-2xl text-rose-900">{t('loveStories.dialogTitle')}</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 mt-4">
                    <div className="space-y-2">
                      <Label className="text-rose-700">Your Names *</Label>
                      <Input
                        value={newStory.couple_names}
                        onChange={(e) => setNewStory({...newStory, couple_names: e.target.value})}
                        placeholder="e.g., Ahmed & Fatima"
                        className="border-rose-200 focus:border-rose-500"
                        data-testid="story-names-input"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label className="text-rose-700">Location</Label>
                        <Input
                          value={newStory.location}
                          onChange={(e) => setNewStory({...newStory, location: e.target.value})}
                          placeholder="City, Country"
                          className="border-rose-200 focus:border-rose-500"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-rose-700">Marriage Date</Label>
                        <Input
                          value={newStory.married_date}
                          onChange={(e) => setNewStory({...newStory, married_date: e.target.value})}
                          placeholder="e.g., March 2024"
                          className="border-rose-200 focus:border-rose-500"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-rose-700">Your Story *</Label>
                      <Textarea
                        value={newStory.story}
                        onChange={(e) => setNewStory({...newStory, story: e.target.value})}
                        placeholder="Tell us how you met and your journey to marriage..."
                        className="min-h-[150px] border-rose-200 focus:border-rose-500"
                        data-testid="story-content-input"
                      />
                    </div>
                    <Button
                      onClick={handleSubmitStory}
                      disabled={submitting}
                      className="w-full bg-gradient-to-r from-rose-500 to-pink-500 text-white rounded-full"
                      data-testid="submit-story-btn"
                    >
                      {submitting ? t('common.submitting') : t('loveStories.submitStory')}
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            )}
          </div>
        </div>
      </nav>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-44 pb-12">
        {/* Hero */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 bg-gradient-to-r from-rose-100 to-pink-100 text-rose-700 px-4 py-2 rounded-full text-sm font-medium mb-4">
            <Heart className="w-4 h-4 fill-rose-500" />
            <span>{t('loveStories.badge')}</span>
          </div>
          <h1 className="font-serif text-4xl sm:text-5xl text-rose-900 mb-4" data-testid="love-stories-heading">
            {t('loveStories.title')}
          </h1>
          <p className="text-rose-600 text-lg max-w-2xl mx-auto">
            {t('loveStories.subtitle')}
          </p>
        </div>

        {/* Stories Grid */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div key={i} className="bg-white rounded-2xl overflow-hidden">
                <div className="h-64 skeleton" />
                <div className="p-6 space-y-3">
                  <div className="h-6 w-40 skeleton rounded" />
                  <div className="h-4 w-24 skeleton rounded" />
                  <div className="h-20 w-full skeleton rounded" />
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-testid="stories-grid">
            {stories.map((story, index) => (
              <div 
                key={story.id}
                className="love-story-card group"
                data-testid={`story-card-${index}`}
              >
                <div className="h-64 overflow-hidden relative">
                  <img 
                    src={story.photo_url}
                    alt={story.couple_names}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-rose-900/60 to-transparent" />
                  <div className="absolute bottom-4 left-4 right-4">
                    <h3 className="font-serif text-2xl text-white mb-1">{story.couple_names}</h3>
                    <div className="flex items-center gap-3 text-rose-200 text-sm">
                      <span className="flex items-center gap-1">
                        <MapPin className="w-3 h-3" />
                        {story.location}
                      </span>
                      <span className="flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        {story.married_date}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="p-6">
                  <div className="flex items-start gap-3">
                    <Quote className="w-6 h-6 text-rose-300 flex-shrink-0 mt-1" />
                    <p className="text-rose-700 text-sm leading-relaxed">{story.story}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* CTA */}
        <div className="mt-16 text-center bg-gradient-to-r from-rose-100 to-pink-100 rounded-3xl p-12">
          <h2 className="font-serif text-2xl text-rose-900 mb-4">{t('loveStories.ctaTitle')}</h2>
          <p className="text-rose-600 mb-6 max-w-lg mx-auto">
            {t('loveStories.ctaSubtitle')}
          </p>
          <Button 
            onClick={() => navigate("/register")}
            className="bg-gradient-to-r from-rose-500 to-pink-500 text-white rounded-full px-8 py-6 text-lg shadow-lg shadow-rose-500/30"
          >
            {t('loveStories.ctaButton')}
            <Heart className="w-5 h-5 ml-2 fill-white" />
          </Button>
        </div>
      </main>
    </div>
  );
};

export default LoveStoriesPage;
